import { supabase } from '@/infrastructure/supabase/client'
import type { IApiKeyRepository, CreateApiKeyResult } from '@/application/port/out/IApiKeyRepository'
import type { ApiKey } from '@brix/types'

function generateKey(): string {
  const bytes = new Uint8Array(32)
  crypto.getRandomValues(bytes)
  const hex = Array.from(bytes).map((b) => b.toString(16).padStart(2, '0')).join('')
  return `brix_sk_${hex}`
}

async function hashKey(key: string): Promise<string> {
  const encoded = new TextEncoder().encode(key)
  const buffer = await crypto.subtle.digest('SHA-256', encoded)
  return Array.from(new Uint8Array(buffer)).map((b) => b.toString(16).padStart(2, '0')).join('')
}

function toApiKey(row: Record<string, unknown>): ApiKey {
  return {
    id: row['id'] as string,
    tenantId: row['tenant_id'] as string,
    name: row['name'] as string,
    keyPrefix: row['key_prefix'] as string,
    scopes: row['scopes'] as string[],
    lastUsedAt: (row['last_used_at'] as string | null) ?? null,
    createdAt: row['created_at'] as string,
  }
}

export class SupabaseApiKeyRepository implements IApiKeyRepository {
  async findByTenantId(tenantId: string): Promise<ApiKey[]> {
    const { data, error } = await supabase
      .from('tenant_api_keys')
      .select('id, tenant_id, name, key_prefix, scopes, last_used_at, created_at')
      .eq('tenant_id', tenantId)
      .order('created_at', { ascending: false })
    if (error) throw new Error(error.message)
    return (data ?? []).map(toApiKey)
  }

  async create(tenantId: string, name: string, scopes: string[]): Promise<CreateApiKeyResult> {
    const rawKey = generateKey()
    const keyHash = await hashKey(rawKey)
    const keyPrefix = rawKey.slice(0, 16) // "brix_sk_" + 8 hex chars

    const { data, error } = await supabase
      .from('tenant_api_keys')
      .insert({ tenant_id: tenantId, name, key_hash: keyHash, key_prefix: keyPrefix, scopes })
      .select('id, tenant_id, name, key_prefix, scopes, last_used_at, created_at')
      .single()
    if (error) throw new Error(error.message)

    return { apiKey: toApiKey(data as Record<string, unknown>), rawKey }
  }

  async delete(id: string): Promise<void> {
    const { error } = await supabase.from('tenant_api_keys').delete().eq('id', id)
    if (error) throw new Error(error.message)
  }
}
